import { Collapse, Group, ScrollArea, Table, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SvgArrowDown } from '@/components/Dashboard/icon';

import classes from './chartTable.module.css';

type CustomValue = string | number;

type ChartTableCustomRowData = {
  type: string;
  value: CustomValue[];
};

type ChartTableRowData = {
  type: string;
  value: { key: string; value: number }[];
  total?: number;
};

interface ChartTableProps {
  title?: string;
  titleRightSection?: React.ReactNode;
  customData?: {
    head: string[];
    body: ChartTableCustomRowData[];
  };
  times?: string[];
  data: ChartTableRowData[];
}

const ChartTable: React.FC<ChartTableProps> = ({ customData, data, times, title, titleRightSection }) => {
  const { t } = useTranslation();
  const { body: customBody, head: customHead } = customData || {};

  const [opened, setOpened] = useState(false);

  const rows = data.map((element, elementIndex) => {
    let total = element.total ? element.total : 0;

    return (
      <Table.Tr key={elementIndex}>
        <Table.Td className="whitespace-nowrap">{element.type}</Table.Td>
        {times &&
          times.map((time, index) => {
            const value = element.value.find((item) => item.key === time)?.value || 0;
            if (!element.total) total += value;
            return (
              <Table.Td key={index} className="text-center">
                {value}
              </Table.Td>
            );
          })}
        <Table.Td className="text-center">{total}</Table.Td>
      </Table.Tr>
    );
  });

  const CustomRows = customBody?.map((element, elementIndex) => {
    return (
      <Table.Tr key={elementIndex}>
        <Table.Td className="whitespace-nowrap text-ellipsis overflow-hidden max-w-[20vw]">
          <Tooltip label={element.type} multiline>
            <span>{element.type}</span>
          </Tooltip>
        </Table.Td>
        {element.value.map((item, index) => {
          return (
            <Table.Td key={index} className="text-center">
              {item}
            </Table.Td>
          );
        })}
      </Table.Tr>
    );
  });

  const CustomHead = () => {
    if (!customHead) return <></>;

    return (
      <>
        {customHead.map((item, index) => {
          return (
            <Table.Th key={index} className="whitespace-nowrap">
              {item}
            </Table.Th>
          );
        })}
      </>
    );
  };

  return (
    <section>
      <Group justify="space-between">
        <UnstyledButton onClick={() => setOpened((o) => !o)}>
          <Group className="rounded-lg cursor-pointer" gap={4}>
            <SvgArrowDown className={`transform transition-transform duration-300 ${!opened ? '-rotate-90' : ''}`} />
            <Text size={'sm'} lh={1.2}>
              {title}
            </Text>
          </Group>
        </UnstyledButton>
        {titleRightSection}
      </Group>

      <Collapse in={opened} mt={16}>
        <ScrollArea style={{ width: '100%' }}>
          <div className="border border-gray-200 rounded-md">
            <Table verticalSpacing="sm" fz="sm" className={`${classes.table} rounded-md`}>
              {customHead ? (
                <>
                  <Table.Thead>
                    <Table.Tr>
                      <CustomHead />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>{CustomRows}</Table.Tbody>
                </>
              ) : (
                <>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th className="whitespace-nowrap">{t('dashboard.series')}</Table.Th>
                      {times &&
                        times.map((item, index) => (
                          <Table.Th key={index} className="whitespace-nowrap" style={{ textAlign: 'center' }}>
                            {item}
                          </Table.Th>
                        ))}

                      <Table.Th className="whitespace-nowrap" style={{ textAlign: 'center' }}>
                        {t('dashboard.total')}
                      </Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>{rows}</Table.Tbody>
                </>
              )}
            </Table>
          </div>
        </ScrollArea>
      </Collapse>
    </section>
  );
};

export default ChartTable;
